<script setup>
import RatingMultiSelect from '@/components/RatingMultiSelect.vue'

const { pivotFields = {}, errors = {}, required = false } = defineProps({
  pivotFields: Object,
  errors: Object,
  /**
   * RED-356 `qv-required-inputs`: every field carries a required mark and a
   * reserved line under it for its inline error. Off = the form as before.
   */
  required: { type: Boolean, default: false },
})

const emit = defineEmits(['update:pivotFields'])

const updateField = (field, value) => {
  const newFields = { ...pivotFields, [field]: value }
  emit('update:pivotFields', newFields)
}
</script>

<template>
  <div data-testid="qv-pivot-form" class="grid grid-cols-1 md:grid-cols-2 gap-5">
    <!-- Building Condition -->
    <div class="group p-4 rounded-xl bg-linear-to-br from-slate-50/80 to-white border border-slate-100 transition-all duration-200 hover:border-slate-200 hover:shadow-xs focus-within:ring-2 focus-within:ring-blue-500/20 focus-within:border-blue-300">
      <label class="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
        <span class="w-6 h-6 rounded-md bg-blue-100 flex items-center justify-center shrink-0">
          <span class="pi pi-building text-blue-600 text-xs"></span>
        </span>
        <span class="flex-1">
          {{ $t('messages.transactions.ehr_fields.ehitise_seisukord') }}
          <span v-if="required" class="text-red-600" :title="$t('messages.quickValuation.requiredMark')" aria-hidden="true">*</span>
        </span>
        <span class="pi pi-info-circle text-slate-300 text-xs cursor-help" v-tooltip="'Hoone üldine tehniline seisukord'"></span>
      </label>
      <RatingMultiSelect
        :model-value="pivotFields.ehitise_seisukord"
        @update:model-value="updateField('ehitise_seisukord', $event)"
        field="ehitise_seisukord"
        data-testid="qv-pivot-ehitise_seisukord"
        :invalid="!!errors.ehitise_seisukord"
        :aria-required="required || undefined"
        :options="[1, 2, 3, 4, 5]"
        :max-selected-labels="2"
        :selection-limit="2"
        placeholder="Vali kuni 2 hinnangut"
        size="base"
      />
      <!-- Reserved height: an error appearing must not shift the grid. -->
      <p
        v-if="required"
        data-testid="qv-pivot-error-ehitise_seisukord"
        class="text-xs mt-1.5 min-h-4"
        :class="errors.ehitise_seisukord ? 'text-red-600' : 'invisible'"
      >
        {{ errors.ehitise_seisukord }}
      </p>
    </div>

    <!-- Apartment Condition -->
    <div class="group p-4 rounded-xl bg-linear-to-br from-slate-50/80 to-white border border-slate-100 transition-all duration-200 hover:border-slate-200 hover:shadow-xs focus-within:ring-2 focus-within:ring-blue-500/20 focus-within:border-blue-300">
      <label class="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
        <span class="w-6 h-6 rounded-md bg-emerald-100 flex items-center justify-center shrink-0">
          <span class="pi pi-home text-emerald-600 text-xs"></span>
        </span>
        <span class="flex-1">
          {{ $t('messages.transactions.ehr_fields.hooneosa_seisukord') }}
          <span v-if="required" class="text-red-600" :title="$t('messages.quickValuation.requiredMark')" aria-hidden="true">*</span>
        </span>
        <span class="pi pi-info-circle text-slate-300 text-xs cursor-help" v-tooltip="'Korteri siseviimistluse ja tehnosüsteemide seisukord'"></span>
      </label>
      <RatingMultiSelect
        :model-value="pivotFields.hooneosa_seisukord"
        @update:model-value="updateField('hooneosa_seisukord', $event)"
        field="hooneosa_seisukord"
        data-testid="qv-pivot-hooneosa_seisukord"
        :invalid="!!errors.hooneosa_seisukord"
        :aria-required="required || undefined"
        :options="[1, 2, 3, 4, 5]"
        :max-selected-labels="2"
        :selection-limit="2"
        placeholder="Vali kuni 2 hinnangut"
        size="base"
      />
      <!-- Reserved height: an error appearing must not shift the grid. -->
      <p
        v-if="required"
        data-testid="qv-pivot-error-hooneosa_seisukord"
        class="text-xs mt-1.5 min-h-4"
        :class="errors.hooneosa_seisukord ? 'text-red-600' : 'invisible'"
      >
        {{ errors.hooneosa_seisukord }}
      </p>
    </div>

    <!-- Storage -->
    <div class="group p-4 rounded-xl bg-linear-to-br from-slate-50/80 to-white border border-slate-100 transition-all duration-200 hover:border-slate-200 hover:shadow-xs focus-within:ring-2 focus-within:ring-blue-500/20 focus-within:border-blue-300">
      <label class="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
        <span class="w-6 h-6 rounded-md bg-amber-100 flex items-center justify-center shrink-0">
          <span class="pi pi-box text-amber-600 text-xs"></span>
        </span>
        <span class="flex-1">
          {{ $t('messages.transactions.ehr_fields.panipaik') }}
          <span v-if="required" class="text-red-600" :title="$t('messages.quickValuation.requiredMark')" aria-hidden="true">*</span>
        </span>
        <span class="pi pi-info-circle text-slate-300 text-xs cursor-help" v-tooltip="'Panipaiga olemasolu ja õiguslik staatus'"></span>
      </label>
      <RatingMultiSelect
        :model-value="pivotFields.panipaik"
        @update:model-value="updateField('panipaik', $event)"
        field="panipaik"
        data-testid="qv-pivot-panipaik"
        :invalid="!!errors.panipaik"
        :aria-required="required || undefined"
        :options="[0, 1, 2]"
        :max-selected-labels="2"
        :selection-limit="2"
        placeholder="Vali kuni 2"
        size="base"
      />
      <!-- Reserved height: an error appearing must not shift the grid. -->
      <p
        v-if="required"
        data-testid="qv-pivot-error-panipaik"
        class="text-xs mt-1.5 min-h-4"
        :class="errors.panipaik ? 'text-red-600' : 'invisible'"
      >
        {{ errors.panipaik }}
      </p>
    </div>

    <!-- Balcony -->
    <div class="group p-4 rounded-xl bg-linear-to-br from-slate-50/80 to-white border border-slate-100 transition-all duration-200 hover:border-slate-200 hover:shadow-xs focus-within:ring-2 focus-within:ring-blue-500/20 focus-within:border-blue-300">
      <label class="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
        <span class="w-6 h-6 rounded-md bg-teal-100 flex items-center justify-center shrink-0">
          <span class="pi pi-sun text-teal-600 text-xs"></span>
        </span>
        <span class="flex-1">
          {{ $t('messages.transactions.ehr_fields.rodu') }}
          <span v-if="required" class="text-red-600" :title="$t('messages.quickValuation.requiredMark')" aria-hidden="true">*</span>
        </span>
        <span class="pi pi-info-circle text-slate-300 text-xs cursor-help" v-tooltip="'Rõdu, terrassi või lodža olemasolu'"></span>
      </label>
      <RatingMultiSelect
        :model-value="pivotFields.rodu"
        @update:model-value="updateField('rodu', $event)"
        field="rodu"
        data-testid="qv-pivot-rodu"
        :invalid="!!errors.rodu"
        :aria-required="required || undefined"
        :options="[0, 1, 2, 3]"
        :max-selected-labels="3"
        :selection-limit="3"
        placeholder="Vali kuni 3"
        size="base"
      />
      <!-- Reserved height: an error appearing must not shift the grid. -->
      <p
        v-if="required"
        data-testid="qv-pivot-error-rodu"
        class="text-xs mt-1.5 min-h-4"
        :class="errors.rodu ? 'text-red-600' : 'invisible'"
      >
        {{ errors.rodu }}
      </p>
    </div>

    <!-- Parking -->
    <div class="md:col-span-2 group p-4 rounded-xl bg-linear-to-br from-slate-50/80 to-white border border-slate-100 transition-all duration-200 hover:border-slate-200 hover:shadow-xs focus-within:ring-2 focus-within:ring-blue-500/20 focus-within:border-blue-300">
      <label class="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
        <span class="w-6 h-6 rounded-md bg-purple-100 flex items-center justify-center shrink-0">
          <span class="pi pi-car text-purple-600 text-xs"></span>
        </span>
        <span class="flex-1">
          {{ $t('messages.transactions.ehr_fields.kinnistu_parkimine') }}
          <span v-if="required" class="text-red-600" :title="$t('messages.quickValuation.requiredMark')" aria-hidden="true">*</span>
        </span>
        <span class="pi pi-info-circle text-slate-300 text-xs cursor-help" v-tooltip="'Parkimisvõimalused kinnistul või hoones'"></span>
      </label>
      <RatingMultiSelect
        :model-value="pivotFields.kinnistu_parkimine"
        @update:model-value="updateField('kinnistu_parkimine', $event)"
        field="kinnistu_parkimine"
        data-testid="qv-pivot-kinnistu_parkimine"
        :invalid="!!errors.kinnistu_parkimine"
        :aria-required="required || undefined"
        :options="[1, 2, 3, 4, 5, 6, 7, 8, 9]"
        :max-selected-labels="3"
        :selection-limit="3"
        placeholder="Vali kuni 3"
        size="base"
      />
      <!-- Reserved height: an error appearing must not shift the grid. -->
      <p
        v-if="required"
        data-testid="qv-pivot-error-kinnistu_parkimine"
        class="text-xs mt-1.5 min-h-4"
        :class="errors.kinnistu_parkimine ? 'text-red-600' : 'invisible'"
      >
        {{ errors.kinnistu_parkimine }}
      </p>
    </div>
  </div>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}
</style>
